/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { DNode } from "@dojo/framework/core/interfaces";
import { BottomRight, TopLeft } from "@dojo/framework/core/meta/Dimensions";
import * as _ from "lodash";
import {
	ANGLE_ZERO,
	BLACK_COLOR,
	CIRCULAR_HANDLE_RADIUS,
	CURVE_CONTROL_POINT_STROKE,
	CURVE_EDIT_STROKE_COLOR,
	CURVE_POINT_DIFF,
	CURVE_SELECTION_STROKE_WIDTH,
	MAX_DIMENSION,
	MAX_Z_INDEX,
	MIN_DIMENSION,
	PERCENTAGE_HUNDRED,
	STROKE_DASH_MAPPING,
} from "../../Constants";
import { mapGroupedPointToShapeMatrix, manipulateDimensions, xor } from "../../store/reducers/util";
import { isClosedShape } from "../../store/SelectionHelper";
import { IExtents } from "../interfaces/model/dataStructures";
import { ICustomShapePoint } from "../interfaces/model/dataStructures/CustomShapePoint";
import { PathOperationTypes } from "../interfaces/model/enumerations/index";
import { ICustomGeometry } from "../interfaces/model/geometry/CustomGeometry";
import {
	IShapePath,
	PathOperation,
} from "../interfaces/model/geometry/ShapePath";
import { IShapePositionCoordinate } from "../interfaces/model/geometry/ShapePositionCoordinate";
import { IBrowserType } from "../interfaces/model/index";
import { IConnectionShape } from "../interfaces/model/PageObjects/ConnectionShape";
import { IShape, IGroupShape } from "../interfaces/model/PageObjects/index";
import { ILineProperties, IShapeProperties, ISolidFillProperties } from "../interfaces/model/properties/index";
import { cancelCurrentOperation } from "../keyUtil";
import CurveTransientPoint from "../meta/CurveTransientPoint";
import { IPosition } from "../meta/Drag/CsiDragController";
import { CsiDrag, IDragResults } from "../meta/Drag/index";
import { getCustomGeometry } from "../selectors/geometry";
import { getZoomValue } from "../selectors/stateAwareConstants";
import * as util from "../util";
import { buildShapePointList, getMappedCurvePoint, getModifiedCurveDelta } from "../util";
import { CurveControlPoint } from "./CurveControlPoint";
import { DraggableWidget } from "./DraggableWidget";

interface IEditCurvePointsProps {
	groupObject: IGroupShape | undefined;

	/** The pageObject corresponding to image */
	pageObject: IShape | IConnectionShape;

	/** Size of the parent widget, it is required to compute offsets for edited shape. */
	parentSize: TopLeft & BottomRight;

	scalePoints: boolean;

	/**
	 * Selected curve point of curve
	 */
	selectedCurvePoint: number | undefined;

	/**
	 * Browser type on which the app is running
	 */
	browserType: IBrowserType;

	/**
	 * Method to be invoked after path list is updated.
	 */
	setCurvePoints(pathLst: IShapePath[], x: number, y: number, width: number, height: number,
		transient?: boolean, offsetX?: number, offsetY?: number): void;

	// Set selected curve point callback
	setSelectedCurvePoint(curvePoint: number | undefined, preserveSelection?: boolean): void;

	//Whether shape is a crop shape
	isCropShape?: boolean;
}

/**
 * Edit curve points component, provides a way to adjust curve points.
 * After each drag setCurvePoints is invoked to update the shape.
 */
export class EditCurvePoints extends DraggableWidget<IEditCurvePointsProps>  {
	private static _SHAPE_TO_EDIT = "curveShape";
	private _boundHandlers: any[];
	private path!: string;
	private geometry!: ICustomGeometry;
	private selectedPathOperationIndex?: number;
	private newPoint!: IPosition;
	private transientPoint?: IPosition;
	private newShapePoint?: ICustomShapePoint;
	private isPointsUpdated = false;
	private isPointsUpdatedInLastRender = false;

	/** Store clone of page, it will be used for all optimistic editing. */
	private pageObject?: IShape | IConnectionShape;

	/** Store custom shape points, all operations are performed on these points */
	private shapePoints: ICustomShapePoint[] = [];

	/** Is dragging in progress, we will be changing stroke color when control point is being dragged. */
	private isDragging = false;

	constructor() {
		super();
		this._boundHandlers = [];
		/** Add event listeners for shape */
		[
			{ event: "pointerup", func: this._onShapePointerUp.bind(this) },
		].forEach((object) => {
			document.addEventListener(object.event, object.func);
			this._boundHandlers.push(object);
		});
	}

	/**
	 * Remove all pointer event listeners on shape
	 */
	protected onDetach(): void {
		this._boundHandlers.forEach((object) => document.removeEventListener(object.event, object.func));
		this.meta(CsiDrag)
			.remove("curveShapeForSelection");
		// set selected curve point to undefined on detached
		this.properties.setSelectedCurvePoint(undefined);
	}

	/**
	 * Run after every renders and return the decorated DNodes
	 * @param dNode The DNodes to run through the after renders
	 */
	protected runAfterRenders(dNode: DNode | DNode[]): DNode | DNode[] {
		// If below condition is true, means this is the second render after the curve point is deleted,
		// where the actual path dimensions are reflected in dom
		if (this.isPointsUpdatedInLastRender && !this.isPointsUpdated) {
			this.saveCurvePoints(true);
			this.isPointsUpdatedInLastRender = false;
		}
		// If last point is delete in edit curve, curve dimension changes so here making sure that render will
		// get called again and on next render saveCurvePoints(above snippet code)
		// will get the correct dimension to calculate the curve height and width.
		if (this.isPointsUpdated) {
			this.isPointsUpdatedInLastRender = true;
			this.isPointsUpdated = false;
			this.saveCurvePoints();
		}

		return dNode;
	}

	/**
	 * Handle pointer down event for shape. This could be start of drag.
	 * Here we want to find out location where new point needs to be added.
	 * Actual point will be added only if drag happens.
	 *
	 * @param event {PointerEvent}
	 */
	protected _onShapePointerDown(event: PointerEvent) {
		// only if add point is allowed
		if (this.pageObject) {
			// mapped the new point with curve
			const mappedLocation = getMappedCurvePoint(this.pageObject, { x: event.pageX, y: event.pageY },
				this.properties.parentSize, this.properties.groupObject);

			const { spPr } = this.pageObject as IShape;

			// new mapped point with zoom value
			this.newPoint = {
				x: mappedLocation.x,
				y: mappedLocation.y,
			};

			// find new index
			this.selectedPathOperationIndex = this._findNewIndex(this.newPoint);
			// select added curve point
			this.properties.setSelectedCurvePoint(this.selectedPathOperationIndex, true);
			if (!this.newShapePoint) {
				this.newShapePoint = {
					isCurvePoint: true,
					position: this.newPoint,
				};
				this.shapePoints.splice(this.selectedPathOperationIndex as number, 0, this.newShapePoint);
				this.isDragging = true;
			}
			// update shape path and geometry
			this.updatePathAndGeometry(spPr);
		}
	}

	/**
	 * Pointer move event added on ghost shape to show the transient point
	 * @param event {PointerEvent}
	 */
	protected _onShapePointerMove(event: PointerEvent) {
		if (this.pageObject && !this.isDragging) {
			const result = this.meta(CurveTransientPoint)
				.get(EditCurvePoints._SHAPE_TO_EDIT, this.pageObject, this.properties.parentSize, this.properties.groupObject, this.shapePoints);
			if (result) {
				this.transientPoint = {
					x: result.best.x,
					y: result.best.y,
				} as IPosition;
				// If transient point is close to curve point with diff CURVE_POINT_DIFF 3 then do not show
				// transient point
				_.some(this.shapePoints, (point: ICustomShapePoint) => {
					if (point.position && this.transientPoint) {
						const pointX = point.position.x as number;
						const pointY = point.position.y as number;
						const transientPoint = this.transientPoint as IPosition;
						if (Math.abs(Math.round(pointX - transientPoint.x)) <= CURVE_POINT_DIFF
							&& Math.abs(Math.round(pointY - transientPoint.y)) <= CURVE_POINT_DIFF) {
							this.transientPoint = undefined;
						}
					}
					return !this.transientPoint;
				});
				this.invalidate();
			}
		}
	}

	/**
	 * Pointer out event added on ghost shape to remove transient point
	 */
	protected _onShapePointerOut() {
		this.transientPoint = undefined;
	}

	/**
	 * Update geometry and path
	 * @param spPr {IShapeProperties}
	 */
	protected updatePathAndGeometry(spPr: IShapeProperties) {
		this.updateGeometry();
		const { cx, cy } = spPr.xfrm.ext;

		const geometryParams = getCustomGeometry({
			cy, cx, geometry: this.geometry,
			shapeAvList: undefined,
		});
		// generate new path
		this.path = geometryParams.pathInfo.pathString;
	}

	/**
	 * Handle pointer up for the shape. This will be end of drag.
	 * We need to preserve the new point added due to drag event. Till now it was optimistic rendering,
	 * here were will call pageAPI to preserve the changes to model.
	 */
	protected _onShapePointerUp() {
		this.addControlPointCompleted();
	}

	/**
	 * Save updated curve points to PageObject model.
	 * @param transient {boolean} transient flag
	 */
	protected saveCurvePoints(transient?: boolean) {
		if (this.pageObject) {
			const { shapePathLst, deltaOffX, deltaOffY, widthDiffPer, heightDiffPer, shapePathDelta } =
				getModifiedCurveDelta(this.pageObject as IShape | IConnectionShape);
			// call action creator to update page model.
			this.properties.setCurvePoints(shapePathLst, deltaOffX, deltaOffY,
				widthDiffPer, heightDiffPer, transient, shapePathDelta.x, shapePathDelta.y);

			// reset all flags for the dragging.
			this.selectedPathOperationIndex = undefined;
			this.newShapePoint = undefined;
			this.pageObject = undefined;
			this.isDragging = false;
		}
	}

	/**
	 * Handle drag for the new point on the shape. We are rendering shape in optimistic way,
	 * shape model is not yet updated.
	 * As we move a new point will be added if it was not added before and then it will be dragged with pointer.
	 * As we drag a point, we need to update it's start & end control points
	 * and also start control point for next operation.
	 *
	 * @param dragResults {IDragResult} Meta corresponding to dragging.
	 * @param isComplete {boolean} drag complete flag.
	 */
	protected onDrag(dragResults: IDragResults, isComplete: boolean) {
		if (this.pageObject && !isComplete) {
			const mappedDelta = mapGroupedPointToShapeMatrix(this.properties.groupObject, this.pageObject, dragResults.delta);
			if (this.selectedPathOperationIndex && this.newShapePoint) {
				this.isDragging = true;
				const controlPointPosition = this.newShapePoint.position as IShapePositionCoordinate;
				controlPointPosition.x = controlPointPosition.x as number + mappedDelta.x;
				controlPointPosition.y = controlPointPosition.y as number + mappedDelta.y;
				// update shape path and geometry
				this.updatePathAndGeometry(this.pageObject.spPr);
			}
		} else {
			// deselect the point if escape key is pressed to end drag
			if (cancelCurrentOperation(dragResults.keyData)) {
				this.properties.setSelectedCurvePoint(undefined);
			}
			this.addControlPointCompleted();
		}
	}

	/**
	 * Get the distance of point <b>p</b> from line p1-p2.
	 * @param p {IPosition} Reference point for which distance from the line to be calculated.
	 * @param p1 {IPosition} Point on curve
	 * @param p2 {IPosition} Point on curve.
	 * @returns {number} distance of P from line p1-p2.
	 */
	protected _distancePointToLine(p: IPosition, p1: IPosition, p2: IPosition) {
		// summary: compute distance from point p to line segment formed by p1 and p2
		const dx = p2.x - p1.x;
		const dy = p2.y - p1.y;
		if (!dx && !dy) {
			return util.getDistance(p, p1);
		}

		const u = (dx * (p.x - p1.x) + dy * (p.y - p1.y)) / (dx * dx + dy * dy);
		if (u < 0) {
			return util.getDistance(p, p1);
		} else if (u > 1) {
			return util.getDistance(p, p2);
		}

		return util.getDistance(p, {
			x: p1.x + u * dx,
			y: p1.y + u * dy,
		});
	};

	/**
	 * Find index for the new point to be added.
	 * @param newLocation {IPosition} Position where new point need to be added.
	 */
	protected _findNewIndex(newLocation: IPosition) {
		const p = newLocation; // util.pointRotate(this.ptInsert, this.ptCenter, -this.radians);
		const n = this.shapePoints.length - 1;
		let dMin = MAX_DIMENSION;
		let d: number;
		let newIndex;

		for (let i = 0; i < n; ++i) {
			d = this._distancePointToLine(p, this.shapePoints[i].position as IPosition,
				this.shapePoints[i + 1].position as IPosition);
			if (d < dMin) {
				dMin = d;
				newIndex = i + 1;
			}
		}
		return newIndex;
	};

	/**
	 * Render shape for dimensions purpose. This shape is not visible, however, is exact copy of actual shape without
	 * rotation or flip applied. Hence, we get accurate dimensions for this shape while in edit. These dimensions are
	 * used for further computations for deciding changes in offsets and extents after edit operations.
	 * @param ext Shape extents
	 */
	protected renderShapeForDimensions(ext: IExtents): DNode | null {
		if (!this.pageObject) {
			return null;
		}
		const height = util.getMaxDimension(ext.cy);
		const width = util.getMaxDimension(ext.cx);
		return (
			<div
				styles={
					{
						height: `${height}px`,
						left: "0px",
						overflow: "visible",
						position: "absolute",
						top: "0px",
						transformOrigin: "center",
						width: `${width}px`,
					}}
			>
				<svg
					height={`${PERCENTAGE_HUNDRED}%`}
					preserveAspectRatio="none"
					styles={{
						overflow: "visible",
						top: "0",
						left: "0",
						position: "absolute",
					}}
					viewBox={`0 0 ${width} ${height}`}
					width={`${PERCENTAGE_HUNDRED}%`}
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d={`${this.path}`}
						fill="none"
						key="curveShapeForDimensions"
						pointer-events="none"
						stroke="none"
						stroke-width={`${CURVE_SELECTION_STROKE_WIDTH}`}
						vector-effect="non-scaling-stroke"
					/>
				</svg>
			</div>
		);
	}

	protected render() {
		// clone page object if it's not cloned already. Clone is reset when operation is complete.
		if (!this.pageObject || (!this.isDragging)) {
			// clone page object
			this.pageObject = _.cloneDeep(this.properties.pageObject);

			// build geometry
			const { spPr } = this.pageObject as IShape;
			this.geometry = spPr.geometry as ICustomGeometry;

			util.updateShapePathList(this.geometry.pathLst, 0, 0);

			this.shapePoints = buildShapePointList(this.geometry);
			// Delete last point if it's CLOSE point, for close point position is undefined
			const lastPoint = this.shapePoints[this.shapePoints.length - 1];
			if (lastPoint && !lastPoint.position) {
				this.shapePoints.pop();
			}
		}
		let scale: string = "";
		const spPr = this.pageObject.spPr;
		let { flipH, rot } = spPr.xfrm;
		const { off, ext } = spPr.xfrm;

		let offX = off.x;
		let offY = off.y;

		if (this.properties.groupObject !== undefined) {
			const resultObj = manipulateDimensions.calculateSubgroupAbsoluteOffsets(spPr, this.properties.groupObject.spPr);

			offX = resultObj.offX;
			offY = resultObj.offY;
			rot = resultObj.rot;

			flipH = xor(flipH, this.properties.groupObject.spPr.xfrm.flipH);
		}

		const geometryParams = getCustomGeometry({
			cx: ext.cx, cy: ext.cy, geometry: this.geometry,
			shapeAvList: undefined,
		});
		this.path = geometryParams.pathInfo.pathString;
		if (flipH) {
			scale = "scale(-1, 1)";
		}
		rot = rot || ANGLE_ZERO;
		const transformOrigin = "center";
		const height = util.getMaxDimension(ext.cy);
		const width = util.getMaxDimension(ext.cx);

		return (
			<div
				key={`pageObject_${this.properties.key}`}
				styles={
					{
						display: "inline-block",
						left: `${offX}px`,
						overflow: "visible",
						position: "absolute",
						top: `${offY}px`,
					}}
			>
				{this.renderShapeForDimensions(ext)}
				<div
					styles={
						{
							height: `${height}px`,
							overflow: "visible",
							position: "absolute",
							transform: `rotate(${rot}deg) ${scale}`,
							transformOrigin: `${transformOrigin}`,
							width: `${width}px`,
							zIndex: `${MAX_Z_INDEX}`,
						}}
				>
					<svg
						height={`${PERCENTAGE_HUNDRED}%`}
						preserveAspectRatio="none"
						styles={{
							overflow: "visible",
							top: "0",
							left: "0",
							position: "absolute",
						}}
						viewBox={`0 0 ${width} ${height}`}
						width={`${PERCENTAGE_HUNDRED}%`}
						xmlns="http://www.w3.org/2000/svg"
					>
						{this.renderShape()}
						<g>
							{this.renderControlPoints()}
						</g>
					</svg>
				</div>
			</div>
		);
	}

	/**
	 * Render the custom shape. This shape is required to handle any new control point addition.
	 *
	 * Here, we render the same shapes with different aspect for different needs.
	 * Consider following layer diagram for further explanation.
	 *
	 * Z index -------------->
	 * |  Page layer                        | Edit layer                                        |
	 * |                                    |                                                   |
	 * ---------------------------------------------------------------------------------------------
	 * |                                    |                                                   |
	 * |  [Curve]     [some other page      | [ Black  | [ White  |  [ optimistic    | [ ghost  |
	 * |               object which is      | stroke   | stroke   |    shape ]       | shape ]  |
	 * |               on top of curve ]    | shape ]  | shape ]  |                  |          |
	 * |     A                  X           |    B     |    W     |      D           |    G     |
	 * |                                    |          |          |                  |          |
	 *
	 * Purpose of each layer ---
	 * A -> Clear purpose - to show curve in the page
	 *
	 * D -> Curve to show when user drags a control point forming change in the actual curve. While dragging, the A
	 * curve is not changed this gives the user the initial state of curve, whereas D gives the probable new state. This
	 * we call as optimistic rendered shape where real shape is not altered until user completes drag by releasing the
	 * control used to change curve.
	 * While dragging, it is drawn with lightSkyblue to show the optimistic rendering.
	 * While not dragging, it is shown with same line colour that of A, however with some transparency. This
	 * transparency creates an illusion as following
	 *   1. The pixels where D and A are intersecting, it results in A only (because of same pixel colour, even though D
	 *      being transparent)
	 *   2. The pixels where D and X are intersecting, it results in X colour with D's transparent factor.
	 * The overall effect is, where back sit curve is visible prior to the curve edit mode is seen as it is, and the
	 * remaining portion (which was hidden cause of back sit) is shown as light transparent above X
	 *
	 * G -> A ghost shape. Not shown to user. This shape provides a "right of way" to add new control points on curve.
	 * Thus, this curve is somewhat bigger (currently, at 18px) in width than actual shape.
	 *
	 * W -> There would be some situations where X and A are of same colour. Yet, to show D in such situation we have
	 * added a contributing layer by using W. This lightens the effect showing D (now W + D) in such situations.
	 *
	 * B -> At times, X and (W+D) will also result in same colour. This is handled with B in same way like W, however,
	 * B is with a unit less that W in thickness. Thus completing the overall issue while showing D (now B + W + D )
	 */
	private renderShape(): DNode[] {
		// Drag meta
		this.requestDragMeta("curveShapeForSelection");

		// Return empty array if there is no pageObject
		if (!this.pageObject) {
			return [];
		}

		const lineProps = this.pageObject.spPr.ln as ILineProperties;
		const lineFill = lineProps && lineProps.fill as ISolidFillProperties;

		let stroke;
		if (this.properties.isCropShape) {
			stroke = BLACK_COLOR;
		} else if (this.isDragging) {
			stroke = CURVE_EDIT_STROKE_COLOR;
		} else {
			stroke = lineFill && lineFill.color || "black";
		}

		// If dragging is in progress, we want to highlight path with different color.
		const opacity = this.isDragging ? 1 : 0.1;
		const strokeWidth = lineProps && lineProps.W || 2;
		// path stroke dasharray
		const dash = lineProps && lineProps.dash || "none";

		const shape = (
			<path
				d={`${this.path}`}
				fill="none"
				key={`${EditCurvePoints._SHAPE_TO_EDIT}`}
				stroke={`${stroke}`}
				stroke-dasharray={`${STROKE_DASH_MAPPING[dash]}`}
				stroke-width={`${strokeWidth}`}
				styles={{ opacity: `${opacity}` }}
				vector-effect="non-scaling-stroke"
			/>
		);
		// Black and white shape stroke added with opacity 0.1 so that it will look as transparent line
		// over all the shapes that overlapping the curve
		const blackStrokeShape = (
			<path
				d={`${this.path}`}
				fill="none"
				key={`${EditCurvePoints._SHAPE_TO_EDIT}`}
				stroke="black"
				stroke-dasharray={`${STROKE_DASH_MAPPING[dash]}`}
				stroke-width={`${strokeWidth - 1}`}
				styles={{ opacity: "0.1" }}
				vector-effect="non-scaling-stroke"
			/>
		);
		const whiteStrokeShape = (
			<path
				d={`${this.path}`}
				fill="none"
				key={`${EditCurvePoints._SHAPE_TO_EDIT}`}
				stroke="white"
				stroke-dasharray={`${STROKE_DASH_MAPPING[dash]}`}
				stroke-width={`${strokeWidth}`}
				styles={{ opacity: "0.1" }}
				vector-effect="non-scaling-stroke"
			/>
		);
		const ghostShape = (
			<path
				d={`${this.path}`}
				fill="none"
				key="curveShapeForSelection"
				onpointerdown={this._onShapePointerDown}
				onpointermove={this._onShapePointerMove}
				onpointerout={this._onShapePointerOut}
				pointer-events="stroke"
				stroke="none"
				stroke-width={`${CURVE_SELECTION_STROKE_WIDTH}`}
				styles={{ cursor: "crosshair" }}
				vector-effect="non-scaling-stroke"
			/>
		);
		return [blackStrokeShape, whiteStrokeShape, shape, ghostShape];
	}

	/**
	 * Render control points for the curve.
	 */
	private renderControlPoints(): DNode[] {
		const { selectedCurvePoint, isCropShape } = this.properties;
		// here we are assuming that we are going to have only single path list.
		const { operations } = this.geometry.pathLst[0];
		const controlPoints = operations.map((operation: PathOperation, index: number) => {
			if (operation.type !== PathOperationTypes.CLOSE) {
				return (
					<CurveControlPoint
						isCropShape={isCropShape}
						isSelectedPoint={selectedCurvePoint === index}
						key={index}
						onPointClick={this.onControlPointClick}
						onPointDrag={this.translateControlPoint}
						onPointDragComplete={this.translateControlPointDone}
						operation={operation}
					/>
				);
			}
		});
		if (this.transientPoint) {
			controlPoints.push(
				<circle
					cx={`${this.transientPoint.x}`}
					cy={`${this.transientPoint.y}`}
					fill={`${CURVE_CONTROL_POINT_STROKE}`}
					key="transient-point"
					r={`${CIRCULAR_HANDLE_RADIUS}`}
					stroke={`${BLACK_COLOR}`}
					stroke-width={`${MIN_DIMENSION}`}
				/>);
		}
		return controlPoints;
	}

	/**
	 * Passed as parameter to adjustable point. This method is invoked on pointer down on the point.
	 * @param data {any} data to identify clicked point.
	 */
	private onControlPointClick(data: any) {
		// set selected curve point
		this.properties.setSelectedCurvePoint(data.operationIndex);
	}

	/**
	 * Passes as parameter to CurveControlPoint. This method is invoked when a control point is dragged.
	 * @param data {any} data to identify clicked point.
	 * @param dx {number} x change in point.
	 * @param dy {number} y change in point.
	 */
	private translateControlPoint(data: any, dx: number, dy: number) {
		// set selected curve point
		if (!this.isDragging) {
			this.properties.setSelectedCurvePoint(data.operationIndex, true);
		}
		this.isDragging = true;
		const index = data.operationIndex;
		const mappedDelta = mapGroupedPointToShapeMatrix(this.properties.groupObject, this.properties.pageObject, { x: dx, y: dy });
		const controlPointPosition = this.shapePoints && this.shapePoints[index] && this.shapePoints[index].position;
		// this.updateControlPoint(shapePath.operations[index], dx, dy);
		if (controlPointPosition) {
			controlPointPosition.x = controlPointPosition.x as number + mappedDelta.x;
			controlPointPosition.y = controlPointPosition.y as number + mappedDelta.y;
		}
		this.updateGeometry(index === this.shapePoints.length);
	};

	/**
	 * Update shape geometry using modified shapePoints.
	 * @param {boolean} isLastPoint
	 */
	private updateGeometry(isLastPoint?: boolean) {
		// clone geometry before we modify it
		this.geometry = _.cloneDeep(this.geometry);
		const shapePathLst = this.geometry.pathLst;
		const shapePath = shapePathLst[0];

		// generate path operation list using shapePoints
		if (this.shapePoints) {
			const newPathOperationList = util.generateShapePathList(this.shapePoints, isLastPoint, isClosedShape(this.pageObject as IShape),
				this.geometry.lineShape);

			shapePath.operations = newPathOperationList;
		}
		// update geometry with new path operation list.
		if (this.pageObject) {
			this.pageObject.spPr.geometry = this.geometry;
		}
	}

	/**
	 * Save curve points once dragging is complete.
	 */
	private translateControlPointDone(data: any, dragResult: IDragResults) {
		// deselect the point if escape key is pressed to end drag
		if (cancelCurrentOperation(dragResult.keyData)) {
			this.properties.setSelectedCurvePoint(undefined);
		}
		this.saveCurvePoints();
	}

	/**
	 * Add control point to curve on drag complete
	 */
	private addControlPointCompleted() {
		if (this.selectedPathOperationIndex) {
			this.saveCurvePoints();
		}
	}
};

export default EditCurvePoints;
